'use client';

import { useCallback, useEffect, useState } from 'react';

/**
 * 웹 푸시 구독 훅 — baikal/rnd/bareunai#264.
 *
 * 원칙:
 *  - **페이지 로드 즉시 권한을 요청하지 않는다.** `subscribe()` 는 사용자가 명시적으로
 *    토글을 켠 순간에만 호출돼야 한다(soft-ask).
 *  - 미지원 브라우저에서는 `supported: false` 로 알려 UI 를 아예 감춘다.
 */

export interface PushDevice {
  id: string;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  failureCount: number;
}

export interface UsePushSubscription {
  /** 브라우저가 웹 푸시를 지원하는가 */
  supported: boolean;
  /** 지원 여부 판정이 끝났는가 (SSR·초기 렌더 중 UI 깜빡임 방지) */
  ready: boolean;
  permission: NotificationPermission | 'unsupported';
  /** 이 브라우저가 현재 구독 중인가 */
  subscribed: boolean;
  /** 서버에 등록된 이 계정의 기기 목록 */
  devices: PushDevice[];
  busy: boolean;
  error: string | null;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  removeDevice: (id: string) => Promise<boolean>;
  refreshDevices: () => Promise<void>;
}

const SW_URL = '/sw.js';

/** VAPID 공개키(base64url)를 applicationServerKey 용 Uint8Array 로 변환한다. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function usePushSubscription(): UsePushSubscription {
  const [supported, setSupported] = useState(false);
  const [ready, setReady] = useState(false);
  const [permission, setPermission] = useState<
    NotificationPermission | 'unsupported'
  >('unsupported');
  const [subscribed, setSubscribed] = useState(false);
  const [devices, setDevices] = useState<PushDevice[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshDevices = useCallback(async () => {
    try {
      const res = await fetch('/api/push/subscribe', { cache: 'no-store' });
      if (!res.ok) return;
      const json = (await res.json()) as { subscriptions?: PushDevice[] };
      setDevices(json.subscriptions ?? []);
    } catch {
      // 목록 조회 실패는 조용히 넘긴다 — 구독 자체를 막을 이유가 없다.
    }
  }, []);

  // 초기 상태 확인. 권한 요청은 하지 않는다(조회만).
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!isPushSupported()) {
        if (!cancelled) {
          setSupported(false);
          setReady(true);
        }
        return;
      }

      if (!cancelled) {
        setSupported(true);
        setPermission(Notification.permission);
      }

      try {
        const registration = await navigator.serviceWorker.getRegistration(SW_URL);
        const existing = await registration?.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!existing);
      } catch {
        if (!cancelled) setSubscribed(false);
      }

      await refreshDevices();
      if (!cancelled) setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshDevices]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    setError(null);

    const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
    if (!vapidPublicKey) {
      setError('서버에 푸시 설정이 없어 알림을 켤 수 없습니다.');
      return false;
    }
    if (!isPushSupported()) {
      setError('이 브라우저는 웹 푸시를 지원하지 않습니다.');
      return false;
    }

    setBusy(true);
    try {
      // 사용자가 토글을 켠 이 순간에만 권한을 요청한다.
      const granted = await Notification.requestPermission();
      setPermission(granted);
      if (granted !== 'granted') {
        setError(
          granted === 'denied'
            ? '브라우저에서 알림이 차단되어 있습니다. 사이트 설정에서 허용해 주세요.'
            : '알림 권한이 필요합니다.'
        );
        return false;
      }

      const registration = await navigator.serviceWorker.register(SW_URL, {
        scope: '/'
      });
      await navigator.serviceWorker.ready;

      // 이미 구독이 있으면 재사용한다(엔드포인트가 바뀌지 않도록).
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        }));

      const res = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription.toJSON())
      });
      if (!res.ok) {
        setError('구독 등록에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return false;
      }

      setSubscribed(true);
      await refreshDevices();
      return true;
    } catch (e) {
      console.error('[push] 구독 실패:', e);
      setError('알림을 켜지 못했습니다. 잠시 후 다시 시도해 주세요.');
      return false;
    } finally {
      setBusy(false);
    }
  }, [refreshDevices]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setError(null);
    setBusy(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration(SW_URL);
      const subscription = await registration?.pushManager.getSubscription();

      if (subscription) {
        await fetch('/api/push/subscribe', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: subscription.endpoint })
        });
        await subscription.unsubscribe();
      }

      setSubscribed(false);
      await refreshDevices();
      return true;
    } catch (e) {
      console.error('[push] 구독 해제 실패:', e);
      setError('알림을 끄지 못했습니다. 잠시 후 다시 시도해 주세요.');
      return false;
    } finally {
      setBusy(false);
    }
  }, [refreshDevices]);

  const removeDevice = useCallback(
    async (id: string): Promise<boolean> => {
      setBusy(true);
      try {
        const res = await fetch('/api/push/subscribe', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id })
        });
        await refreshDevices();
        return res.ok;
      } finally {
        setBusy(false);
      }
    },
    [refreshDevices]
  );

  return {
    supported,
    ready,
    permission,
    subscribed,
    devices,
    busy,
    error,
    subscribe,
    unsubscribe,
    removeDevice,
    refreshDevices
  };
}
